<template>
	<div class="business-logs-container">
		<AppTable ref="logs-table"
				  :fetch-function="getBusinessLogs"
				  :filters="filters"
				  :table-headings="tableHeadings"
				  :title="getString('business_logs')"
				  :total-items="0"
				  :items="logs || []"
				  initial-sort-field="date"  >
			<template v-slot:custom-pagination>
				<div class="row space-between centered">
					<div class="go-back go-direction" @click="currNextKeyIndex > 0 ? getBusinessLogs({before: true}) : null">
						<AppChevronIcon v-if="currNextKeyIndex > 0" direction="left" />
					</div>
					<div class="go-forward go-direction" @click="currNextKeyIndex <= nextKeys.length-1 ? getBusinessLogs({after: true}) : null">
						<AppChevronIcon v-if="currNextKeyIndex <= nextKeys.length-1" direction="right" />
					</div>
				</div>
			</template>
		</AppTable>
	</div>
</template>

<script>
import AppTable from "@/shared/components/AppTable.vue";
import {useLanguageStore} from "@/shared/stores";
import {useUserStore} from "@/console-new/stores";
import API from "@/shared/helpers/API";
import {getCompatibleDate} from "@/shared/helpers/dates";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		return {
			getString: languageStore.getString,
			userStore,
		}
	},
	name: "BusinessLogs",
	components: {AppChevronIcon, AppTable},
	data() {
		return {
			logs: [],
			tableHeadings: [
				{
					text: this.getString('date'),
					cols: 1,
					fieldName: 'date',
				},
				{
					text: this.getString('user'),
					cols: 1,
					fieldName: 'user_name',
				},
				{
					text: this.getString('type'),
					cols: 1,
					fieldName: 'type_string',
				},
				{
					text: this.getString('log'),
					cols: 3,
					fieldName: 'log',
				},
			],
			filters: null,
			nextKeys: [],
			currNextKeyIndex: 0,
		}
	},
	methods: {
		getBusinessLogs(filters, callback = null) {
			if (!this.userStore.currBusinessId) {
				if (callback) {
					callback(false);
				}
				return;
			}

			let lastKeyToUse = null;
			if(filters && filters.after) {
				if(this.currNextKeyIndex < this.nextKeys.length) {
					lastKeyToUse = this.nextKeys[this.currNextKeyIndex];
					this.currNextKeyIndex++;
				} else {
					lastKeyToUse = null;
					this.currNextKeyIndex = 0;
					this.nextKeys = [];
				}
			} else if(filters && filters.before) {
				if(this.currNextKeyIndex > 1) {
					lastKeyToUse = this.nextKeys[this.currNextKeyIndex - 2];
					this.currNextKeyIndex--;
				} else if(this.currNextKeyIndex > 0) {
					lastKeyToUse = null;
					this.currNextKeyIndex--;
				} else {
					lastKeyToUse = null;
					this.currNextKeyIndex = 0;
					this.nextKeys = [];
				}
			} else {
				lastKeyToUse = filters && filters.last_key ? filters.last_key : null;
				this.currNextKeyIndex = 0;
				this.nextKeys = [];
			}
			let currNextKeyIndex = this.currNextKeyIndex;

			API.init().getBusinessLogs(this.userStore.currBusinessId, "2023-09-01 00:00:00", null, lastKeyToUse, r => {
				this.logs = [];
				if (r && r.result === "OK") {
					if (r.logs && r.logs && r.logs.length) {
						let users = r.users || {};
						r.logs.forEach(t => {
							let log = this.normalizeLog(t, users);
							if(log) {
								this.logs.push(log);
							}
						});
						if(r.last_key && currNextKeyIndex === this.currNextKeyIndex && this.currNextKeyIndex === this.nextKeys.length) {
							this.nextKeys.push(JSON.stringify(r.last_key));
						}
					}
					if (callback) {
						callback(true);
					}
				} else {
					if (callback) {
						callback(false, r ? r.details : null);
					}
				}
			});

		},
		normalizeLog(log, users) {
			let timeEvent = log.time_event && log.time_event.S ? log.time_event.S.split("#") : [];
			if(timeEvent.length === 3) {
				let date = new Date(parseInt(timeEvent[1]) * 1000);
				log.date = getCompatibleDate(date);
				if(log.user_id && log.user_id.N) {
					let userId = Number(log.user_id.N);
					if(userId && users.hasOwnProperty(userId)) {
						log.user_name = users[userId];
					}
				} else {
					log.user_name = "-";
				}
				if(log.type && log.type.S) {
					log.type = log.type.S;
				} else {
					log.type = "-";
				}
				log.type_string = this.getString(log.type);
				log.log = this.getLogText(log);
				return log;
			} else {
				return null;
			}
		},
		getLogText(log) {
			let logText = "";
			if (log.type === "business_update") {
				let field = log.field && log.field.S ? log.field.S : "-";
				let oldValue = log.old_value && log.old_value.S ? log.old_value.S : "-";
				let newValue = log.new_value && log.new_value.S ? log.new_value.S : "-";
				logText = this.getString('log_business_update', [field, oldValue, newValue]);
			} else if (log.type === "coupon_delete") {
				let coupon = log.coupon && log.coupon.S ? log.coupon.S : "-";
				logText = this.getString('log_coupon_delete', [coupon]);
			} else if (log.type === "coupon_add") {
				let coupon = log.coupon && log.coupon.S ? log.coupon.S : "-";
				if(log?.verification_codes_count?.N) {
					let additionalCodesCount = log.verification_codes_count.N;
					logText = this.getString('log_coupon_with_additional_codes_add', [coupon, additionalCodesCount]);
				} else {
					logText = this.getString('log_coupon_add', [coupon]);
				}
			} else if (log.type === "printer_add") {
				let sn = log.sn && log.sn.S ? log.sn.S : "-";
				logText = this.getString('log_printer_add', [sn]);
			} else if (log.type === "printer_delete") {
				let printer = log.printer && log.printer.S ? log.printer.S : "-";
				logText = this.getString('log_printer_delete', [printer]);
			} else if (log.type === "printer_update") {
				let printer = log.printer && log.printer.S ? log.printer.S : "-";
				let field = log.field && log.field.S ? log.field.S : "-";
				let oldValue = log.old_value && log.old_value.S ? log.old_value.S : "-";
				let newValue = log.new_value && log.new_value.S ? log.new_value.S : "-";
				logText = this.getString('log_printer_update', [printer, field, oldValue, newValue]);
			} else if (log.type === "printer_update_categories") {
				let printer = log.printer && log.printer.S ? log.printer.S : "-";
				let count = log.count && log.count.N ? log.count.N : "-";
				logText = this.getString('log_printer_update_categories', [printer, count]);
			} else if (log.type === "category_update_printers") {
				let category = log.category && log.category.S ? log.category.S : "-";
				logText = this.getString('log_category_update_printers', [category]);
			} else if (log.type === "permission_update") {
				let userUpdate = log.user_update && log.user_update.S ? log.user_update.S : "-";
				let permission = log.permission && log.permission.S ? log.permission.S : "-";
				if(log.active && log.active.S) {
					if(log.active.S === "true") {
						logText = this.getString('log_permission_add', [permission, userUpdate]);
					} else {
						logText = this.getString('log_permission_remove', [permission, userUpdate]);
					}
				}
			} else if (log.type === "permission_new") {
				let userEmail = log.email && log.email.S ? log.email.S : "-";
				let permissions = log.permissions && log.permissions.S ? log.permissions.S : "-";
				let exists = log.exists && log.exists.S ? log.exists.S : "-";
				if(exists === "true") {
					logText = this.getString('log_permission_new', [userEmail, permissions]);
				} else {
					logText = this.getString('log_permission_new_invited', [userEmail, permissions]);
				}
			} else if (log.type === "couriers_update") {
				let couriers = log.couriers && log.couriers.S ? log.couriers.S : "-";
				logText = this.getString('log_couriers_update', [couriers]);
			} else if (log.type === "removed_terminal_hidden_users") {
				let removedHiddenEmails = "-";
				if(log?.emails?.S?.startsWith && log.emails.S.startsWith('[')) {
					removedHiddenEmails = JSON.parse(log.emails.S).toString();
				}
				logText = this.getString('removed_terminal_hidden_users_text', [log?.terminal_name?.S || '-', removedHiddenEmails]);
			} else if (log.type === "added_terminal_hidden_users") {
				let addedHiddenEmails = "-";
				if(log?.emails?.S?.startsWith && log.emails.S.startsWith('[')) {
					addedHiddenEmails = JSON.parse(log.emails.S).toString();
				}
				logText = this.getString('added_terminal_hidden_users_text', [log?.terminal_name?.S || '-', addedHiddenEmails]);
			} else if (log.type === "room_add") {
				let room = log.room && log.room.S ? log.room.S : "-";
				logText = this.getString('log_room_add', [room]);
			} else if (log.type === "table_add") {
				let room = log.room && log.room.S ? log.room.S : "-";
				let table = log.table && log.table.S ? log.table.S : "-";
				logText = this.getString('log_table_add', [table, room]);
			} else if (log.type === "room_update") {
				let room = log.room && log.room.S ? log.room.S : "-";
				let old_room = log.old_room && log.old_room.S ? log.old_room.S : "-";
				let room_type = log.room_type && log.room_type.S ? log.room_type.S : "-";
				let room_accept_orders = log.room_accept_orders && log.room_accept_orders.S ? log.room_accept_orders.S : "-";
				let room_enabled_sms = log.room_enabled_sms && log.room_enabled_sms.S ? log.room_enabled_sms.S : "-";
				let room_enabled_calls = log.room_enabled_calls && log.room_enabled_calls.S ? log.room_enabled_calls.S : "-";
				let room_notification_sound = log.room_notification_sound && log.room_notification_sound.S ? log.room_notification_sound.S : "-";
				let room_deleted = log.room_deleted && log.room_deleted.S ? log.room_deleted.S : "-";
				if(room_deleted === "true") {
					logText = this.getString('log_room_delete', [old_room]);
				} else {
					logText = this.getString('log_room_update', [old_room, room, room_type, room_accept_orders, room_enabled_sms, room_enabled_calls, room_notification_sound]);
				}
			} else if(log.type === "table_update") {
				let table = log.table && log.table.S ? log.table.S : "-";
				let old_table = log.old_table && log.old_table.S ? log.old_table.S : "-";
				let room = log.room && log.room.S ? log.room.S : "-";
				let table_deleted = log.table_deleted && log.table_deleted.S ? log.table_deleted.S : "-";
				if(table_deleted === "true") {
					logText = this.getString('log_table_delete', [old_table, room]);
				} else {
					logText = this.getString('log_table_update', [old_table, room, table]);
				}
			} else if (log.type === "menu_add") {
				logText = this.getString('log_menu_add');
			} else if (log.type === "menu_order_update") {
				logText = this.getString('log_menu_order_update');
			} else if (log.type === "menu_delete") {
				let menu = log.menu && log.menu.S ? log.menu.S : "-";
				logText = this.getString('log_menu_delete', [menu]);
			} else if (log.type === "menu_update") {
				let menu = log.menu && log.menu.S ? log.menu.S : "-";
				let changes = log.changes && log.changes.S ? log.changes.S : "-";
				changes = JSON.parse(changes);
				changes = Object.entries(changes).map(([key, value]) => {
					return this.getString(key) + " '" + value + "'";
				}).join(", ");
				logText = this.getString('log_menu_update', [menu, changes]);
			} else if (log.type === "category_add") {
				let category = log.category && log.category.S ? log.category.S : "-";
				logText = this.getString('log_category_add', [category]);
			} else if (log.type === "category_delete") {
				let category = log.category && log.category.S ? log.category.S : "-";
				logText = this.getString('log_category_delete', [category]);
			} else if (log.type === "category_update") {
				let category = log.category && log.category.S ? log.category.S : "-";
				let changes = log.changes && log.changes.S ? log.changes.S : "-";
				changes = JSON.parse(changes);
				changes = Object.entries(changes).map(([key, value]) => {
					return this.getString(key) + " '" + value + "'";
				}).join(", ");
				logText = this.getString('log_category_update', [category, changes]);
			} else if (log.type === "item_add") {
				let item = log.item && log.item.S ? log.item.S : "-";
				logText = this.getString('log_item_add', [item]);
			} else if (log.type === "lists_update") {
				let item = log.item && log.item.S ? log.item.S : "-";
				logText = this.getString('log_lists_update', [item]);
			} else if (log.type === "item_update") {
				let item = log.item && log.item.S ? log.item.S : "-";
				let changes = log.changes && log.changes.S ? log.changes.S : "-";
				changes = JSON.parse(changes);
				changes = Object.entries(changes).map(([key, value]) => {
					return this.getString(key) + " '" + value + "'";
				}).join(", ");
				logText = this.getString('log_item_update', [item, changes]);
			} else if (log.type === "coupon_verification_codes_update") {
				let couponCode = log?.coupon_code?.S ? log.coupon_code.S : "-";
				let couponName = log?.coupon_name?.S ? log.coupon_name.S : "-";
				let codesCount = log?.codes_count?.N ? log.codes_count.N : "0";
				logText = this.getString('log_coupon_verification_codes_update', [couponName, couponCode, codesCount]);
			}
			return logText;
		},
	},
}
</script>

<style scoped lang="scss">
.go-direction {
	cursor: pointer;
	padding: 8px;

}
</style>